import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import {
  Loader2, Search, Check, X, Copy, ExternalLink, ImageOff, Filter,
  Sparkles, AlertTriangle, RefreshCw, Package, Tag, Percent, PlugZap, Shuffle,
  Palette as PaletteIcon, Megaphone, ChevronDown, ChevronUp, Trash2, FileCode2, Type,
  Plus, ArrowLeft, ArrowRight, MousePointerClick, Rocket, Clock, ArrowDownUp, Maximize2,
  Upload, PencilRuler,
} from 'lucide-react';
import FrameEditor from './frameEditor/FrameEditor';

const API = 'https://api.glowytics.com';
const BASE = `${API}/api/feed-builder`;

const trMoney = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 });
const fmtPrice = (v, cur) => {
  if (v == null) return '—';
  const sym = cur === 'TRY' || cur === 'TL' ? '₺' : (cur ? cur + ' ' : '');
  return sym + trMoney.format(Math.round(v));
};

const HEX = /^#[0-9a-fA-F]{6}$/;

// Sihirbaz adımları — ürün seç → çerçeve seç → görünüm → üret
const STEPS = [
  { n: 1, label: 'Products', hint: 'Pick what goes into the feed' },
  { n: 2, label: 'Frame', hint: 'See each template on your own product' },
  { n: 3, label: 'Look', hint: 'Colours, logo, campaign text' },
  { n: 4, label: 'Build', hint: 'Name it, generate the XML' },
];

/* Kapaktaki kareler — hepsi GERÇEK çıktı, sahte mockup DEĞİL.
   ⚠️ URL'lerdeki ?v= damgası ŞART: dosya adları sabit, api.glowytics.com uzun süre
   önbellekliyor — damga artmadan görseli değiştirirsen kimse yenisini görmez.
   Akış (Efe, 2026-09-02): ham kare Gemini'ye OLDUĞU GİBİ verilip lifestyle sahne
   yaptırılıyor (kesim/PNG derdi yok), 3. kare o sahnenin üstüne `scene` şablonuyla
   basılıyor. Üretim: glowytics-backend/scripts/glowy_studio_hero/ */
/* Başlık ikonu — Gemini'yle üretilmiş 3B ikon (lucide `Layers` yerine geçti).
   🔑 Zemini saydam; gölgeyi PNG değil CSS `drop-shadow` verir — modelin kendi
   gölgesini bırakınca panelde açık gri bir KUTU olarak görünüyordu.
   🔴 `?v=` damgası ŞART: dosya adı sabit, /pixel uzun önbellekliyor.
   Alternatifler aynı klasörde: icon-foto · icon-sihir · icon-yigin
   (değiştirmek için tek satır: aşağıdaki dosya adını değiştir + v'yi artır). */
const HERO_ICON = `${API}/pixel/glowy-studio/icon-cerceve.png?v=2`;

/* Kapaktaki demo ürün ve AKIŞ. 🔴🔴 14 Eyl 2026, Efe iki tur döndü:
   1. tur "tasarım çok AI gözüküyor, bu parfüm olayı hiç olmadı, bir sürü
      yeteneğimiz var hiçbirini gösterememişiz" → ürün değişti (kırmızı stand
      mikser) ve 2. kare AI'ın çizdiği ÇERÇEVE oldu.
   2. tur "çerçeve yerine bir mutfakta doğal bir şekilde yerleştir · ürün ismini
      ortala, fiyatın tam orta sırasına denk gelsin, ismi büyüt" → 2. kare
      SAHNE oldu, bileşenler sahnenin ÜZERİNE basılıyor.
   Akış: 1) ham katalog karesi  2) doğal sahne  3) + bileşenler
   (alt bant · ad · üstü çizili eski fiyat · fiyat · indirim rozeti).
   🔑🔑 3. kare motorun KENDİ `render_design`'ından çıkıyor — kapaktaki yazı
   düzeni birebir kullanıcının alacağı çıktı, temsilî görsel değil.
   🔑 Ürün üç kez elendi (ayakkabı·yüzük·parfüm): küçük kadrajda tek parça net
   silueti ve doygun rengi olan ürün gerekiyor. Yedek takım hazır: `lamba`.
   Değiştirmek = aşağıdaki tek kelime + `?v=` artışı.
   Üretim: scripts/glowy_studio_hero/{gen_urun2.py,build_hero2.py},
   kaynaklar kaynak2/ (ham kare + sahne). */
const HERO_DEMO = 'mikser';
const HERO_V = 9;
const demoSrc = (k) => `${API}/pixel/glowy-studio/demo-${HERO_DEMO}-${k}.jpg?v=${HERO_V}`;

const HERO_ROWS = [
  {
    key: 'demo',
    arrows: ['in a scene', 'components'],
    shots: [
      { n: 1, src: demoSrc('ham'), light: true,
        title: 'Raw catalogue image', hint: 'Exactly as it lands from Merchant Center' },
      { n: 2, src: demoSrc('sahne'),
        title: 'Placed in a real setting', hint: 'The same product, photographed into a kitchen' },
      { n: 3, src: demoSrc('bilesen'),
        title: 'Components on top', hint: 'Name, was-price, price and the discount badge' },
    ],
  },
];

/* Başlık cümlesinin İÇİNE giren 3B ikon kutucukları (14 Eyl 2026, Efe'nin
   referansı `image copy 208.png`: pastel yuvarlak kare + içinde parlak 3B cisim,
   kelimenin hemen yanında). 🔑 Ölçü `em` cinsinden — başlık mobilde küçülünce
   ikon da küçülsün; `align` ile taban çizgisine oturur. 🔑 Gölgeyi PNG değil
   CSS verir (zemin saydam kesildi, bkz. cut_icon.py).
   Üretim: gen_icon.py --serit (adaylar: foto · etiket · mutfak · firca). */
const HERO_ICONS = { foto: 'serit-foto', etiket: 'serit-etiket' };
function HeroIcon({ k }) {
  return (
    <img
      src={`${API}/pixel/glowy-studio/${HERO_ICONS[k]}.png?v=1`}
      alt=""
      aria-hidden="true"
      className="inline-block h-[1.3em] w-[1.3em] align-[-0.3em] select-none"
      style={{ filter: 'drop-shadow(0 3px 6px rgba(15,23,42,0.16))' }}
    />
  );
}

/** Seçilen dosyanın en-boy oranı — yükleme popup'ındaki slot kontrolü için.
    🔑 Kontrol UYARIR, ENGELLEMEZ: asıl denetim backend'de (FRAME_SPEC); burada
    amaç yanlış kutuya bırakılan dosyayı yüklemeden önce söylemek. */
function readAspect(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img.naturalWidth / img.naturalHeight); };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
}

const UPLOAD_SLOTS = [
  { key: '1:1', label: 'Square · 1:1', hint: 'Feed and catalogue placements', want: 1 },
  { key: '4:5', label: 'Portrait · 4:5', hint: 'Feed and Reels placements', want: 0.8 },
];

/* Referanstaki elle çizilmiş his: tek kavisli çizgi + ayrı uç. */
function HeroArrow({ label }) {
  return (
    <div className="shrink-0 flex flex-col items-center justify-center w-20 lg:w-28 pb-10">
      <span className="text-[12px] lg:text-[13px] font-medium text-gray-500 whitespace-nowrap mb-0.5">
        {label}
      </span>
      <svg viewBox="0 0 120 40" className="w-full text-gray-400" fill="none" aria-hidden="true">
        <path d="M5 31 C 28 9, 76 6, 105 22" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
        <path d="M92 13 L108 23 L90 30" stroke="currentColor" strokeWidth="2.4"
              strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

function HeroShot({ shot }) {
  // açık zeminli kareler kapak zemininde kaybolmasın diye kenarı biraz daha belirgin
  const ring = shot.light ? 'ring-1 ring-gray-200' : 'ring-1 ring-black/5';
  return (
    <figure className="flex-1 min-w-0 max-w-[260px] group">
      <div className={`relative rounded-2xl overflow-hidden bg-white ${ring}
                      shadow-[0_18px_40px_-12px_rgba(15,23,42,0.28)]
                      transition-transform duration-300 group-hover:-translate-y-1`}>
        <img src={shot.src} alt={shot.title} loading="lazy"
             className="block w-full aspect-[7/9] object-cover" />
        <span className="absolute top-2.5 left-2.5 w-6 h-6 rounded-full bg-white/95 backdrop-blur
                         text-[11px] font-bold text-gray-700 flex items-center justify-center
                         shadow-sm ring-1 ring-black/5">
          {shot.n}
        </span>
      </div>
      <figcaption className="mt-2.5 text-center">
        <div className="text-[13px] font-semibold text-gray-900 leading-tight">{shot.title}</div>
        <div className="text-[11px] text-gray-500 leading-snug mt-0.5">{shot.hint}</div>
      </figcaption>
    </figure>
  );
}

export default function FeedBuilder() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id;
  const headers = useMemo(
    () => (brandId ? { 'X-Brand-ID': brandId, 'Content-Type': 'application/json' } : null),
    [brandId]
  );
  // Dosya yüklemede Content-Type ELLE verilmez: multipart sınırını (boundary)
  // tarayıcı üretir, elle 'multipart/form-data' yazarsan istek okunamaz.
  const brandHeader = useMemo(() => (brandId ? { 'X-Brand-ID': brandId } : null), [brandId]);

  const [status, setStatus] = useState(null);
  const [products, setProducts] = useState([]);
  const [facets, setFacets] = useState({ categories: [], labels: [], total: 0, withDiscount: 0 });
  const [templates, setTemplates] = useState([]);
  const [palette, setPalette] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // sihirbaz
  const [wizard, setWizard] = useState(false);
  const [step, setStep] = useState(1);

  // görünüm ayarları — şablon ve renk BAĞIMSIZ
  const [templateId, setTemplateId] = useState(null);
  const [colors, setColors] = useState(['#111d3c', '#28478c']);
  const [useLogo, setUseLogo] = useState(false);
  const [logoUrl, setLogoUrl] = useState('');
  // Yükleme popup'ı: kare ve 4:5 çerçeve AYRI alınır (Efe, 14 Eyl). Meta'da iki
  // oran ayrı yaratıcı; tek dosyayı esnetmek tasarımı bozuyor.
  const [uploadOpen, setUploadOpen] = useState(false);
  const [logoError, setLogoError] = useState(null);   // logo indirilemedi (403 vb.)
  const [logoNote, setLogoNote] = useState(null);    // logo nereden bulundu
  const [logoBusy, setLogoBusy] = useState(false);
  const logoFileRef = useRef(null);
  // AI çerçevesine isim/fiyat/rozet de bassın mı (Efe: "Puffy'de yaptığımız gibi
  // akıllı bir şeyler üretmeli"). Varsayılan AÇIK; çıktı bir tasarım belgesi
  // olduğu için kullanıcı tuval editöründe her şeyi değiştirebilir.
  const [aiComponents, setAiComponents] = useState(true);
  const [aiHeadline, setAiHeadline] = useState('');
  const [upFiles, setUpFiles] = useState({ '1:1': null, '4:5': null });
  const [priceColor, setPriceColor] = useState('');      // '' → şablon varsayılanı
  const [allVariants, setAllVariants] = useState(false); // false → renk başına tek varyant
  const [feeds, setFeeds] = useState([]);
  const [slugTouched, setSlugTouched] = useState(false);

  // kampanya metinleri (varsayılan kapalı)
  const [campOpen, setCampOpen] = useState(false);
  const [camp, setCamp] = useState({
    badgeTop: '', badgeNum: '', badgeBottom: '',
    line1: '', pct: '', suffix: '',      // boş → motor alt kelimeyi hiç basmaz
    promoPre: '', promoHi: '',
  });

  // önizleme ürünü + galeri
  const [sample, setSample] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);

  // filtre + seçim
  const [q, setQ] = useState('');
  const [idPanelOpen, setIdPanelOpen] = useState(false);
  const [idText, setIdText] = useState('');
  const [cats, setCats] = useState(new Set());
  const [labels, setLabels] = useState(new Set());
  const [discountOnly, setDiscountOnly] = useState(false);
  const [pickedOnly, setPickedOnly] = useState(false);
  const [picked, setPicked] = useState(new Set());

  // ek filtreler (hepsi tarayıcı tarafında, /products çıktısı üzerinde)
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [minDiscount, setMinDiscount] = useState('');   // en az % indirim
  const [nameIn, setNameIn] = useState('');             // isimde geçsin (virgülle çoklu = herhangi biri)
  const [nameOut, setNameOut] = useState('');           // isimde geçmesin
  const [brands, setBrands] = useState(new Set());
  const [colorSel, setColorSel] = useState(new Set());
  const [minImages, setMinImages] = useState(1);
  const [gtinOnly, setGtinOnly] = useState(false);
  const [multiVariantOnly, setMultiVariantOnly] = useState(false);
  const [sortBy, setSortBy] = useState('name');
  const [topN, setTopN] = useState('50');
  const [zoom, setZoom] = useState(null);   // büyütülen şablon önizlemesi

  // kullanıcının kendi yüklediği çerçeveler
  const frameFileRef = useRef(null);       // kare (1:1) dosya seçici
  const frameFileRef45 = useRef(null);     // dikey (4:5) dosya seçici
  const [frameFit, setFrameFit] = useState('contain');  // contain: kırpma yok | cover: pencereyi doldur
  const [frameUploading, setFrameUploading] = useState(false);
  const [frameReject, setFrameReject] = useState(null); // { errors[], spec[] } — reddedilen yükleme
  const [frameSpec, setFrameSpec] = useState([]);       // kabul kuralları (backend'den)
  const [frameRev, setFrameRev] = useState(0);          // yükleme/silme sonrası galeriyi tazeler

  // kendi çerçeveni TASARLA — tuval editörü (ürün alanı + logo + yazı)
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorFrame, setEditorFrame] = useState(null);  // {id,name} verilirse DÜZENLEME

  // AI ile çerçeve üretimi — tarif → 3 aday → biri seçilir
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiRatio, setAiRatio] = useState('1:1');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiDrafts, setAiDrafts] = useState(null);       // [{ok, draftId, url, errors}]
  const [aiAdopting, setAiAdopting] = useState(null);   // seçilmekte olan draftId

  // üretim
  const [slug, setSlug] = useState('');
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);
  const galleryReq = useRef(0);

  const effLogo = useLogo ? (logoUrl || status?.brand?.logoUrl || '') : '';

  const campaignPayload = useMemo(() => {
    const c = {};
    if (camp.badgeNum || camp.badgeTop || camp.badgeBottom) {
      c.badgeLines = [
        camp.badgeTop && [camp.badgeTop, 22, 700],
        camp.badgeNum && [camp.badgeNum, 66, 800],
        camp.badgeBottom && [camp.badgeBottom, 30, 800],
      ].filter(Boolean);
    }
    if (camp.line1) c.line1 = camp.line1;
    if (camp.pct) c.pct = camp.pct;
    if (camp.suffix) c.suffix = camp.suffix;
    if (camp.promoPre) c.promoPre = camp.promoPre;
    if (camp.promoHi) c.promoHi = camp.promoHi;
    return Object.keys(c).length ? c : null;
  }, [camp]);

  // ---------------------------------------------------------------- yükleme
  const load = useCallback(async () => {
    if (!headers) return;
    setLoading(true); setError(null); setPicked(new Set());
    try {
      const sr = await fetch(`${BASE}/status`, { credentials: 'include', headers });
      const st = await sr.json();
      if (!sr.ok) throw new Error(st.error || 'Could not load status');
      setStatus(st);
      if (st.brand?.logoUrl) setLogoUrl((c) => c || st.brand.logoUrl);
      if (!st.ready) { setProducts([]); return; }

      const [pr, tp, fr] = await Promise.all([
        fetch(`${BASE}/products${allVariants ? '?allVariants=1' : ''}`, { credentials: 'include', headers }),
        fetch(`${BASE}/templates`, { credentials: 'include', headers }),
        fetch(`${BASE}/feeds`, { credentials: 'include', headers }),
      ]);
      const pj = await pr.json(); const tj = await tp.json();
      try { const fj = await fr.json(); setFeeds(fj.feeds || []); } catch { /* liste opsiyonel */ }
      if (!pr.ok) throw new Error(pj.error || 'Could not load products');
      if (!tp.ok) throw new Error(tj.error || 'Could not load templates');

      setProducts(pj.products || []);
      setFacets(pj.facets || { categories: [], labels: [], total: 0, withDiscount: 0 });
      setTemplates(tj.templates || []);
      setPalette(tj.palette || []);
      // varsayılan seçim hazır şablonlardan olsun — kullanıcının yüklediği
      // çerçeveler listenin başında duruyor, ama seçimi kullanıcı yapmalı
      setTemplateId((c) => c || (tj.templates || []).find((t) => !t.custom)?.id
        || tj.templates?.[0]?.id || null);
      // önizleme için görseli bol bir ürün seç
      const best = [...(pj.products || [])].sort((a, b) => b.imageCount - a.imageCount)[0];
      setSample((c) => c || best || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [headers, allVariants]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => clearInterval(pollRef.current), []);

  // sihirbaz açıkken arka planı kaydırma
  useEffect(() => {
    if (!wizard) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setWizard(false); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [wizard]);

  // --------------------------------------------------- galeri (tek üründe hepsi)
  // Sadece sihirbazın 2. adımından itibaren basılır — ürün seçerken boşuna render yok.
  useEffect(() => {
    if (!headers || !sample || !wizard || step < 2) return;
    const id = ++galleryReq.current;
    const t = setTimeout(async () => {
      setGalleryLoading(true);
      try {
        const qs = new URLSearchParams({ groupId: sample.groupId, colors: colors.join(',') });
        if (effLogo) qs.set('logo', effLogo);
        if (campaignPayload) qs.set('campaign', JSON.stringify(campaignPayload));
        if (priceColor) qs.set('priceColor', priceColor);
        const r = await fetch(`${BASE}/preview?${qs}`, { credentials: 'include', headers });
        const j = await r.json();
        if (galleryReq.current !== id) return;      // eski istek geç geldiyse yut
        if (!r.ok) throw new Error(j.error || 'Could not render the preview');
        setGallery(j.previews || []);
        // 🔑 Logo alınamadıysa SEBEBİNİ göster: eskiden kare sessizce logosuz
        // basılıyordu, kullanıcı "Add logo"ya basıp hiçbir şey olmadığını
        // görüyordu (Efe bildirdi).
        setLogoError(j.logoError || null);
      } catch (e) {
        if (galleryReq.current === id) toast.error(e.message);
      } finally {
        if (galleryReq.current === id) setGalleryLoading(false);
      }
    }, 350);   // renk/metin değişiminde ardışık istekleri engelle
    return () => clearTimeout(t);
  }, [headers, sample, colors, effLogo, campaignPayload, priceColor, wizard, step, frameRev]);

  // -------------------------------------------------- kendi çerçeveni yükle
  //
  // Çerçeve = saydam pencereli PNG kaplama. Motor pencereye YALNIZ ürün
  // görselini basar — isim/fiyat/logo eklemez, tasarım pişmiş kabul edilir.
  // Standart dışı dosya sessizce düzeltilmez; backend 422 + hangi kuralın
  // düştüğü ile döner, o liste kullanıcıya aynen gösterilir.

  const refreshTemplates = useCallback(async () => {
    if (!headers) return;
    try {
      const r = await fetch(`${BASE}/templates`, { credentials: 'include', headers });
      const j = await r.json();
      if (r.ok) setTemplates(j.templates || []);
    } catch { /* liste tazelenemezse eski liste kalır */ }
  }, [headers]);

  // kabul kuralları backend'den — kuralı iki yerde yazıp ayrışmalarını engeller
  useEffect(() => {
    if (!headers) return;
    let alive = true;
    fetch(`${BASE}/frames`, { credentials: 'include', headers })
      .then((r) => r.json())
      .then((j) => { if (alive) setFrameSpec(j.spec || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [headers]);

  // 🔑🔑 Markanın logosunun YEREL kopyası (`/brand-logo`). Kayıtlı logo adresi
  // bazı markalarda bot koruması arkasında (403) ve motor da render sırasında
  // aynı duvara toslayıp logoyu sessizce basmıyordu. Yerel kopya varsa onu
  // kullanırız — hem hızlı hem de kaynak adres ölse bile ayakta.
  useEffect(() => {
    if (!headers) return;
    let alive = true;
    fetch(`${BASE}/brand-logo`, { credentials: 'include', headers })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!alive || !j?.asset?.url) return;
        setLogoUrl(j.asset.url);
        setLogoError(null);
        setLogoNote(j.source === 'discovered'
          ? 'Found automatically on your site — upload your own file if this is not the right mark.'
          : null);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [headers]);

  // Logoyu dosyadan markaya SAKLA. Adresi bot koruması arkasında olan
  // markalarda (sunucumuzun IP'si engelli) tek yol bu — bir kez yapılır.
  const uploadBrandLogo = useCallback(async (file) => {
    if (!file || !brandHeader) return;
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${BASE}/brand-logo`, {
        method: 'POST', credentials: 'include', headers: brandHeader, body: fd,
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error((j.errors || [])[0] || j.error || 'Could not save the logo'); return; }
      setLogoUrl(j.asset.url);
      setUseLogo(true);
      setLogoError(null);
      setLogoNote(null);
      toast.success('Logo saved for this brand');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLogoBusy(false);
      if (logoFileRef.current) logoFileRef.current.value = '';
    }
  }, [brandHeader]);

  const uploadFrame = useCallback(async (file, ratioLabel = '') => {
    if (!file || !brandHeader) return false;
    setFrameReject(null);
    setFrameUploading(true);
    try {
      const fd = new FormData();
      fd.append('frame', file);
      fd.append('fit', frameFit);
      // 🔑 Ad'a oran eki: aynı tasarımın kare ve 4:5 hâli galeride ayırt edilsin.
      fd.append('name', file.name.replace(/\.[^.]+$/, '') + (ratioLabel ? ` · ${ratioLabel}` : ''));
      const r = await fetch(`${BASE}/frames`, {
        method: 'POST', credentials: 'include', headers: brandHeader, body: fd,
      });
      const j = await r.json();
      if (!r.ok) {
        // 422 → standart dışı: sebepleri panelde göster, toast'ta kısaca özetle
        if (j.errors?.length) {
          setFrameReject({ errors: j.errors, spec: j.spec || frameSpec });
          toast.error(j.error || 'Frame rejected');
        } else {
          toast.error(j.error || 'Upload failed');
        }
        return false;
      }
      await refreshTemplates();
      setTemplateId(j.frame.id);
      setFrameRev((v) => v + 1);      // galeriyi yeniden bastır
      toast.success(ratioLabel ? `Frame uploaded (${ratioLabel})` : 'Frame uploaded');
      return true;
    } catch (e) {
      toast.error(e.message);
      return false;
    } finally {
      setFrameUploading(false);
      if (frameFileRef.current) frameFileRef.current.value = '';   // aynı dosya tekrar seçilebilsin
    }
  }, [brandHeader, frameFit, frameSpec, refreshTemplates]);

  // Tarife göre üç aday çizdirir. Model saydam PNG üretemediği için motor
  // pencereyi kroma yeşiliyle isteyip sonra saydama çeviriyor — burada tek
  // görünen şey üç aday. Denetimden düşen aday da sebebiyle gösterilir.
  const generateFrames = useCallback(async () => {
    if (!brandHeader || aiPrompt.trim().length < 8) return;
    setAiBusy(true);
    setAiDrafts(null);
    setFrameReject(null);
    try {
      const r = await fetch(`${BASE}/frames/generate`, {
        method: 'POST', credentials: 'include',
        headers: { ...brandHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: aiPrompt.trim(), ratio: aiRatio, fit: frameFit }),
      });
      const j = await r.json();
      if (!r.ok) { toast.error(j.error || 'Could not generate'); return; }
      setAiDrafts(j.drafts || []);
      const good = (j.drafts || []).filter((d) => d.ok).length;
      if (!good) toast.error('None of the three came out usable — try a different brief.');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setAiBusy(false);
    }
  }, [brandHeader, aiPrompt, aiRatio, frameFit]);

  // Seçilen aday, yüklenmiş çerçeveyle BİREBİR aynı yola girer (custom:<uuid>)
  const adoptFrame = useCallback(async (draft) => {
    if (!brandHeader || !draft?.draftId) return;
    setAiAdopting(draft.draftId);
    try {
      const r = await fetch(`${BASE}/frames/adopt`, {
        method: 'POST', credentials: 'include',
        headers: { ...brandHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          draftId: draft.draftId, fit: frameFit,
          // eş oran (1:1 ↔ 4:5) da kaydedilsin
          pairDraftId: draft.pair?.draftId || '',
          name: aiPrompt.trim().slice(0, 40) || 'AI frame',
          components: aiComponents,
          headline: aiComponents ? aiHeadline.trim() : '',
        }),
      });
      const j = await r.json();
      if (!r.ok) {
        if (j.errors?.length) setFrameReject({ errors: j.errors, spec: j.spec || frameSpec });
        toast.error(j.error || 'Could not save');
        return;
      }
      await refreshTemplates();
      setTemplateId(j.frame.id);
      setFrameRev((v) => v + 1);
      setAiDrafts(null);
      setAiOpen(false);
      toast.success((j.frames || []).length > 1
        ? 'Both frames added — square and 4:5'
        : 'Frame added');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setAiAdopting(null);
    }
  }, [brandHeader, frameFit, aiPrompt, frameSpec, refreshTemplates, aiComponents, aiHeadline]);

  // Editörden dönen çerçeve, yüklenen/AI çerçevesiyle AYNI yola girer
  // (custom:<uuid>) — burada yapılacak tek şey listeyi tazeleyip onu seçmek.
  const onDesignSaved = useCallback(async (frame) => {
    await refreshTemplates();
    if (frame?.id) setTemplateId(frame.id);
    setFrameRev((v) => v + 1);          // galeriyi yeni çerçeveyle yeniden bastır
    setEditorFrame(null);
  }, [refreshTemplates]);

  // Editörde canlı görünecek örnek ürünler: seçim varsa ONDAN, yoksa
  // katalogdan. Liste küçük tutuluyor, editör içinden rastgele biri seçiliyor.
  const editorProducts = useMemo(() => {
    const withImg = products.filter((p) => p.thumb);
    const inPick = withImg.filter((p) => picked.has(p.groupId));
    return (inPick.length ? inPick : withImg).slice(0, 60);
  }, [products, picked]);

  const deleteFrame = useCallback(async (tplId) => {
    if (!headers) return;
    const id = tplId.replace(/^custom:/, '');
    try {
      const r = await fetch(`${BASE}/frames/${id}`, {
        method: 'DELETE', credentials: 'include', headers,
      });
      const j = await r.json();
      if (!r.ok) { toast.error(j.message || j.error || 'Could not delete'); return; }
      await refreshTemplates();
      setTemplateId((c) => (c === tplId ? null : c));
      setFrameRev((v) => v + 1);
      toast.success('Frame deleted');
    } catch (e) {
      toast.error(e.message);
    }
  }, [headers, refreshTemplates]);

  // ---------------------------------------------------------------- filtre
  const num = (v) => {
    const n = parseFloat(String(v).replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  };
  const words = (v) => String(v).split(',').map((w) => w.trim().toLocaleLowerCase('tr')).filter(Boolean);
  const effPrice = (p) => (p.salePrice != null ? p.salePrice : p.price);
  const discPct = (p) => (p.salePrice != null && p.price ? Math.round((1 - p.salePrice / p.price) * 100) : 0);

  // katalogdaki fiyat aralığı — alan etiketlerinde ipucu olarak gösterilir
  const priceBounds = useMemo(() => {
    const vals = products.map(effPrice).filter((v) => v != null);
    if (!vals.length) return null;
    return { min: Math.min(...vals), max: Math.max(...vals) };
  }, [products]);

  // marka facet'i backend'den gelmiyor — ürün listesinden çıkarılır (çok markalı kataloglar için)
  const brandFacet = useMemo(() => {
    const m = new Map();
    products.forEach((p) => { if (p.brand) m.set(p.brand, (m.get(p.brand) || 0) + 1); });
    return [...m.entries()].map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
  }, [products]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLocaleLowerCase('tr');
    const inW = words(nameIn), outW = words(nameOut);
    const pMin = num(priceMin), pMax = num(priceMax), dMin = num(minDiscount);
    const out = products.filter((p) => {
      if (pickedOnly && !picked.has(p.groupId)) return false;
      if (cats.size && !cats.has(p.category || '')) return false;
      if (labels.size && !p.labels.some((l) => labels.has(l))) return false;
      if (brands.size && !brands.has(p.brand || '')) return false;
      if (colorSel.size && !colorSel.has(p.color || '')) return false;
      if (p.imageCount < minImages) return false;
      if (multiVariantOnly && (p.variantCount || 1) < 2) return false;
      if (gtinOnly && !p.gtin) return false;
      if (discountOnly && p.salePrice == null) return false;
      if (dMin != null && discPct(p) < dMin) return false;

      const price = effPrice(p);
      if (pMin != null && (price == null || price < pMin)) return false;
      if (pMax != null && (price == null || price > pMax)) return false;

      if (inW.length || outW.length) {
        const hay = `${p.title || ''} ${p.series || ''}`.toLocaleLowerCase('tr');
        if (inW.length && !inW.some((w) => hay.includes(w))) return false;
        if (outW.length && outW.some((w) => hay.includes(w))) return false;
      }

      if (!needle) return true;
      return (p.series || '').toLocaleLowerCase('tr').includes(needle)
        || (p.title || '').toLocaleLowerCase('tr').includes(needle)
        || (p.offerId || '').toLocaleLowerCase('tr').includes(needle)
        || (p.groupId || '').toLocaleLowerCase('tr').includes(needle)
        || (p.offerIds || []).some((id) => String(id).toLocaleLowerCase('tr').includes(needle));
    });

    const cmp = {
      name: (a, b) => (a.series || '').localeCompare(b.series || '', 'tr'),
      priceAsc: (a, b) => (effPrice(a) ?? Infinity) - (effPrice(b) ?? Infinity),
      priceDesc: (a, b) => (effPrice(b) ?? -Infinity) - (effPrice(a) ?? -Infinity),
      discount: (a, b) => discPct(b) - discPct(a),
      images: (a, b) => b.imageCount - a.imageCount,
    }[sortBy];
    return cmp ? [...out].sort(cmp) : out;
  }, [products, q, cats, labels, brands, colorSel, minImages, multiVariantOnly, gtinOnly,
      discountOnly, minDiscount, priceMin, priceMax, nameIn, nameOut, sortBy, pickedOnly, picked]);

  // panelde "N filtre aktif" rozetini besler
  const activeFilterCount =
    (q.trim() ? 1 : 0) + cats.size + labels.size + brands.size + colorSel.size +
    (priceMin ? 1 : 0) + (priceMax ? 1 : 0) + (minDiscount ? 1 : 0) +
    (nameIn.trim() ? 1 : 0) + (nameOut.trim() ? 1 : 0) +
    (minImages > 1 ? 1 : 0) + (gtinOnly ? 1 : 0) + (multiVariantOnly ? 1 : 0) + (discountOnly ? 1 : 0);

  const resetFilters = () => {
    setQ(''); setCats(new Set()); setLabels(new Set()); setBrands(new Set()); setColorSel(new Set());
    setPriceMin(''); setPriceMax(''); setMinDiscount(''); setNameIn(''); setNameOut('');
    setMinImages(1); setGtinOnly(false); setMultiVariantOnly(false); setDiscountOnly(false);
    setPickedOnly(false);
  };

  // feed adı: şablon + renk + aktif filtrelerden otomatik türetilir, elle ezilebilir
  const paletteName = useMemo(() => {
    const hit = palette.find((p) => p.colors.join() === colors.join());
    return hit ? hit.name : 'ozel-renk';
  }, [palette, colors]);

  const autoSlug = useMemo(() => {
    if (slugTouched && slug) return slug;
    const tpl = templates.find((t) => t.id === templateId);
    const bits = [tpl?.name || templateId, paletteName];
    if (cats.size) bits.push([...cats].slice(0, 2).join('-') || 'kategori');
    if (labels.size) bits.push([...labels][0]);
    if (brands.size) bits.push([...brands][0]);
    if (q.trim()) bits.push(q.trim());
    if (nameIn.trim()) bits.push(words(nameIn)[0]);
    if (priceMin || priceMax) bits.push(`${priceMin || 'alt'}-${priceMax || 'ust'}-tl`);
    if (minDiscount) bits.push(`${minDiscount}-uzeri-indirim`);
    if (discountOnly) bits.push('indirimli');
    if (allVariants) bits.push('tum-varyantlar');
    return bits.filter(Boolean).join('-')
      .toLowerCase()
      .replace(/ğ/g, 'g').replace(/ü/g, 'u').replace(/ş/g, 's')
      .replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ç/g, 'c')
      .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
      .slice(0, 70) || 'feed';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slugTouched, slug, templates, templateId, paletteName, cats, labels, brands, q, nameIn,
      priceMin, priceMax, minDiscount, discountOnly, allVariants]);

  const deleteFeed = async (fslug) => {
    if (!headers || !window.confirm(`Delete the "${fslug}" feed and all of its images?`)) return;
    try {
      const r = await fetch(`${BASE}/feeds/${fslug}`, { method: 'DELETE', credentials: 'include', headers });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Silinemedi');
      setFeeds((f) => f.filter((x) => x.slug !== fslug));
      toast.success('Silindi');
    } catch (e) { toast.error(e.message); }
  };

  const activeTemplate = templates.find((t) => t.id === templateId);
  const needImgs = activeTemplate?.images || 1;
  const pickedList = useMemo(() => products.filter((p) => picked.has(p.groupId)), [products, picked]);
  const thinPicks = pickedList.filter((p) => p.imageCount < needImgs).length;
  const activePreview = gallery.find((g) => g.templateId === templateId);

  const toggleIn = (setter) => (v) => setter((s) => {
    const n = new Set(s); n.has(v) ? n.delete(v) : n.add(v); return n;
  });
  const toggle = toggleIn(setPicked);
  const toggleCat = toggleIn(setCats);
  const toggleLabel = toggleIn(setLabels);
  const toggleBrand = toggleIn(setBrands);
  const toggleColor = toggleIn(setColorSel);

  const selectAllFiltered = () => setPicked((s) => {
    const n = new Set(s); filtered.forEach((p) => n.add(p.groupId)); return n;
  });
  const clearFiltered = () => setPicked((s) => {
    const n = new Set(s); filtered.forEach((p) => n.delete(p.groupId)); return n;
  });
  // sıralamayla birlikte kullanışlı: "en pahalı 50 ürünü seç" gibi
  const selectTopN = () => {
    const n = parseInt(topN, 10);
    if (!Number.isFinite(n) || n <= 0) { toast.error('Enter a valid number'); return; }
    setPicked((s) => {
      const next = new Set(s); filtered.slice(0, n).forEach((p) => next.add(p.groupId)); return next;
    });
    toast.success(`Selected the first ${Math.min(n, filtered.length)} in this order`);
  };

  // yapıştırılan ID / URL listesinden ürün seç (URL'deki -p-14429182 gibi ID'ler de yakalanır)
  const selectByIds = () => {
    const ids = [...new Set(idText.match(/\d{4,}/g) || [])];
    if (!ids.length) { toast.error('No product IDs found in that text'); return; }
    const hitGroups = new Set();
    const foundIds = new Set();
    products.forEach((p) => {
      const own = new Set([...(p.offerIds || []), p.offerId].filter(Boolean).map(String));
      const m = ids.filter((id) => own.has(id) || (p.groupId || '').includes(id));
      if (m.length) { hitGroups.add(p.groupId); m.forEach((id) => foundIds.add(id)); }
    });
    if (!hitGroups.size) { toast.error('None of those IDs matched this brand’s products'); return; }
    setPicked((s) => new Set([...s, ...hitGroups]));
    setPickedOnly(true);   // ızgarada sadece seçilenler görünsün
    const missing = ids.filter((id) => !foundIds.has(id));
    if (missing.length) {
      toast(`${hitGroups.size} products selected · unmatched IDs: ${missing.slice(0, 5).join(', ')}${missing.length > 5 ? '…' : ''}`, { icon: '⚠️' });
    } else {
      toast.success(`${hitGroups.size} products selected (${ids.length} IDs matched)`);
    }
  };

  // önizleme ürünü: 2. adımdan sonra SEÇİLENLER arasından gelir
  const randomSample = () => {
    const pool = pickedList.length ? pickedList : (filtered.length ? filtered : products);
    if (pool.length) setSample(pool[Math.floor(Math.random() * pool.length)]);
  };

  // ---------------------------------------------------------------- sihirbaz
  const openWizard = () => {
    setJob(null);
    setStep(1);
    setPicked(new Set());
    setPickedOnly(false);
    setWizard(true);
  };

  const canNext =
    step === 1 ? picked.size > 0 :
    step === 2 ? !!templateId :
    true;

  const goNext = () => {
    if (!canNext) return;
    if (step === 1) {
      // önizleme, seçilen ürünlerin görseli en bol olanıyla yapılsın
      if (!picked.has(sample?.groupId)) {
        const best = [...pickedList].sort((a, b) => b.imageCount - a.imageCount)[0];
        if (best) setSample(best);
      }
    }
    setStep((s) => Math.min(4, s + 1));
  };
  const goBack = () => setStep((s) => Math.max(1, s - 1));

  // ---------------------------------------------------------------- üretim
  const generate = async () => {
    if (!headers || !templateId || picked.size === 0) return;
    setJob({ status: 'running', total: picked.size });
    try {
      const r = await fetch(`${BASE}/generate`, {
        method: 'POST', credentials: 'include', headers,
        body: JSON.stringify({
          templateId, groupIds: [...picked], slug: autoSlug, name: autoSlug,
          colors, logoUrl: effLogo || null, campaign: campaignPayload,
          priceColor: priceColor || null, allVariants,
          filters: {
            q: q || null,
            categories: [...cats],
            labels: [...labels],
            brands: [...brands],
            colors: [...colorSel],
            nameIn: nameIn || null,
            nameOut: nameOut || null,
            priceMin: num(priceMin),
            priceMax: num(priceMax),
            minDiscount: num(minDiscount),
            minImages, gtinOnly, multiVariantOnly, discountOnly,
          },
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Could not start the build');

      clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const s = await fetch(`${BASE}/generate/${j.jobId}`, { credentials: 'include', headers });
          const sj = await s.json();
          setJob(sj);
          if (sj.status === 'done' || sj.status === 'error') {
            clearInterval(pollRef.current);
            if (sj.status === 'done') {
              toast.success(`Feed ready — ${sj.count} products`);
              fetch(`${BASE}/feeds`, { credentials: 'include', headers })
                .then((r) => r.json()).then((f) => setFeeds(f.feeds || [])).catch(() => {});
            }
            else toast.error(sj.error || 'Build failed');
          }
        } catch { /* geçici poll hatası */ }
      }, 2500);
    } catch (e) {
      setJob({ status: 'error', error: e.message });
      toast.error(e.message);
    }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success('Copied'); };
  const setColor = (i, v) => setColors((c) => (i ? [c[0], v] : [v, c[1]]));

  // ---------------------------------------------------------------- durumlar
  if (!brandId) return <div className="p-8 text-gray-500">No brand selected.</div>;

  if (status && !status.ready) {
    return (
      <div className="p-8 max-w-xl mx-auto text-center">
        <PlugZap className="w-10 h-10 text-gray-300 mx-auto mb-3" />
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Glowy Studio</h2>
        <p className="text-sm text-gray-500 mb-4">
          {!status.hasConnection
            ? 'This brand has no active Merchant Center connection. The studio runs on synced Merchant Center products.'
            : 'Merchant Center is connected, but no product data has synced yet.'}
        </p>
        <button onClick={load} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
          Yeniden dene
        </button>
      </div>
    );
  }

  const campFields = activeTemplate?.campaign || [];

  // ============================================================== ADIM 1: ÜRÜN
  const stepProducts = (
    <div className="grid grid-cols-[275px_1fr] gap-5 h-full min-h-0">
      <div className="h-full min-h-0 flex flex-col">
        <div className="border border-gray-200 rounded-xl p-3.5 flex-1 min-h-0 overflow-y-auto">
          <div className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Filter className="w-4 h-4" /> Filters
            {activeFilterCount > 0 && (
              <>
                <span className="px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-bold">
                  {activeFilterCount}
                </span>
                <button onClick={resetFilters} className="ml-auto text-[11px] text-gray-500 hover:text-gray-800 underline">
                  clear
                </button>
              </>
            )}
          </div>
          <div className="relative mb-3">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by product name or ID…"
              className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            {q && <button onClick={() => setQ('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>}
          </div>

          <div className="mb-3 pb-3 border-b border-gray-100">
            <button onClick={() => setIdPanelOpen((o) => !o)}
              className="text-xs text-indigo-600 hover:underline flex items-center gap-1">
              Select by ID list
              {idPanelOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            {idPanelOpen && (
              <div className="mt-2">
                <textarea value={idText} onChange={(e) => setIdText(e.target.value)} rows={4}
                  placeholder={"Product IDs or product links —\none per line, or comma separated"}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y" />
                <button onClick={selectByIds} disabled={!idText.trim()}
                  className="mt-1 w-full py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed">
                  Select matches
                </button>
              </div>
            )}
          </div>

          {/* fiyat */}
          <div className="mb-3 pb-3 border-b border-gray-100">
            <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Price</div>
            <div className="flex items-center gap-1.5">
              <input value={priceMin} onChange={(e) => setPriceMin(e.target.value)} inputMode="decimal"
                placeholder={priceBounds ? `min ${Math.floor(priceBounds.min)}` : 'min'}
                className="w-full min-w-0 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              <span className="text-gray-400 text-xs">—</span>
              <input value={priceMax} onChange={(e) => setPriceMax(e.target.value)} inputMode="decimal"
                placeholder={priceBounds ? `max ${Math.ceil(priceBounds.max)}` : 'max'}
                className="w-full min-w-0 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
            <p className="text-[10px] text-gray-400 mt-1 leading-snug">
              Discounted products are matched on their sale price.
            </p>
          </div>

          {/* indirim */}
          <div className="mb-3 pb-3 border-b border-gray-100">
            <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Discount</div>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer mb-2">
              <input type="checkbox" checked={discountOnly} onChange={(e) => setDiscountOnly(e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
              <Percent className="w-3.5 h-3.5 text-gray-400" /> On sale only ({facets.withDiscount})
            </label>
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-500 shrink-0">At least</span>
              <input value={minDiscount} onChange={(e) => setMinDiscount(e.target.value)} inputMode="numeric"
                placeholder="20"
                className="w-16 px-2 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              <span className="text-xs text-gray-500">% off</span>
            </div>
          </div>

          {/* isim */}
          <div className="mb-3 pb-3 border-b border-gray-100">
            <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Product name</div>
            <input value={nameIn} onChange={(e) => setNameIn(e.target.value)}
              placeholder="Must contain: bed, base"
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg mb-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            <input value={nameOut} onChange={(e) => setNameOut(e.target.value)}
              placeholder="Must not contain: spare, accessory"
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            <p className="text-[10px] text-gray-400 mt-1 leading-snug">
              Comma separated. “Must contain” matches any of them, “must not contain” removes all of them.
            </p>
          </div>

          <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Category</div>
          <div className="space-y-1 mb-3 max-h-52 overflow-y-auto pr-1">
            {facets.categories.map((c) => {
              // 🔑 Motor boş kategoriyi "(no category)" diye adlandırıyor;
              // eski Türkçe etiket de gelebilsin diye ikisi de boş sayılır.
              const key = (c.name === '(no category)' || c.name === '(kategorisiz)') ? '' : c.name;
              const on = cats.has(key);
              return (
                <button key={c.name} onClick={() => toggleCat(key)}
                  className={`w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-sm transition ${
                    on ? 'bg-indigo-600 text-white' : 'hover:bg-gray-100 text-gray-700'}`}>
                  <span className="truncate">{c.name}</span>
                  <span className={`text-xs shrink-0 ml-2 ${on ? 'text-indigo-100' : 'text-gray-400'}`}>{c.count}</span>
                </button>
              );
            })}
          </div>

          {brandFacet.length > 1 && (
            <div className="mb-3">
              <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Brand</div>
              <div className="flex flex-wrap gap-1 max-h-28 overflow-y-auto">
                {brandFacet.slice(0, 30).map((b) => {
                  const on = brands.has(b.name);
                  return (
                    <button key={b.name} onClick={() => toggleBrand(b.name)}
                      className={`px-2 py-0.5 rounded-full text-[11px] border transition ${
                        on ? 'bg-indigo-600 text-white border-indigo-600' : 'border-gray-300 text-gray-600 hover:bg-gray-100'}`}>
                      {b.name.length > 22 ? b.name.slice(0, 22) + '…' : b.name} · {b.count}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {facets.colors?.length > 0 && (
            <div className="mb-3">
              <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Colour</div>
              <div className="flex flex-wrap gap-1 max-h-28 overflow-y-auto">
                {facets.colors.slice(0, 30).map((c) => {
                  const on = colorSel.has(c.name);
                  return (
                    <button key={c.name} onClick={() => toggleColor(c.name)}
                      className={`px-2 py-0.5 rounded-full text-[11px] border transition ${
                        on ? 'bg-indigo-600 text-white border-indigo-600' : 'border-gray-300 text-gray-600 hover:bg-gray-100'}`}>
                      {c.name.length > 20 ? c.name.slice(0, 20) + '…' : c.name} · {c.count}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {facets.labels?.length > 0 && (
            <>
              <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5 flex items-center gap-1">
                <Tag className="w-3 h-3" /> Custom label
              </div>
              <div className="flex flex-wrap gap-1 mb-3 max-h-32 overflow-y-auto">
                {facets.labels.slice(0, 15).map((l) => {
                  const on = labels.has(l.name);
                  return (
                    <button key={l.name} onClick={() => toggleLabel(l.name)}
                      className={`px-2 py-0.5 rounded-full text-[11px] border transition ${
                        on ? 'bg-indigo-600 text-white border-indigo-600' : 'border-gray-300 text-gray-600 hover:bg-gray-100'}`}>
                      {l.name.length > 24 ? l.name.slice(0, 24) + '…' : l.name} · {l.count}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          <div className="mb-3 pb-3 border-b border-gray-100">
            <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Variants</div>
            <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" checked={!allVariants}
                onChange={(e) => setAllVariants(!e.target.checked)}
                className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
              <span>
                One variant per colour
                <span className="block text-[11px] text-gray-500 leading-snug">
                  One product per colour is rendered (the cheapest size). Turn it off and every size enters separately —
                  the list reloads and your selection is cleared.
                </span>
              </span>
            </label>
            {facets.withColor === 0 && !allVariants && (
              <p className="text-[11px] text-amber-700 mt-1.5 leading-snug">
                This brand’s feed carries no colour data — de-duplication falls back to the product group.
              </p>
            )}
          </div>

          <div className="mb-3">
            <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Other</div>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-xs text-gray-500">At least</span>
              <select value={minImages} onChange={(e) => setMinImages(Number(e.target.value))}
                className="px-2 py-1 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500">
                {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <span className="text-xs text-gray-500">images or more</span>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer mb-1.5">
              <input type="checkbox" checked={multiVariantOnly} onChange={(e) => setMultiVariantOnly(e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
              Multi-variant only (2+)
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" checked={gtinOnly} onChange={(e) => setGtinOnly(e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
              With a GTIN only
            </label>
          </div>
        </div>
      </div>

      <div className="h-full min-h-0 flex flex-col">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap shrink-0">
          <div className="text-sm text-gray-600 flex items-center gap-2">
            <Package className="w-4 h-4 text-gray-400" />
            <span className="font-semibold text-gray-900">{filtered.length}</span> products
            {filtered.length !== facets.total && <span className="text-gray-400">/ {facets.total}</span>}
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <div className="flex items-center gap-1.5">
              <ArrowDownUp className="w-3.5 h-3.5 text-gray-400" />
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                className="px-2 py-1.5 text-xs border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option value="name">Name (A→Z)</option>
                <option value="priceAsc">Price: low to high</option>
                <option value="priceDesc">Price: high to low</option>
                <option value="discount">Discount (high → low)</option>
                <option value="images">Image count (many → few)</option>
              </select>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-500">First</span>
              <input value={topN} onChange={(e) => setTopN(e.target.value)} inputMode="numeric"
                className="w-14 px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              <button onClick={selectTopN}
                className="px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50">
                select
              </button>
            </div>
            <button onClick={() => setPickedOnly((o) => !o)} disabled={!picked.size && !pickedOnly}
              className={`px-3 py-1.5 text-xs rounded-lg border transition disabled:opacity-40 disabled:cursor-not-allowed ${
                pickedOnly ? 'bg-indigo-600 text-white border-indigo-600' : 'border-gray-300 hover:bg-gray-50'}`}>
              Selected only ({picked.size})
            </button>
            <button onClick={selectAllFiltered} className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50">
              Select all shown ({filtered.length})
            </button>
            <button onClick={clearFiltered} className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50">
              Clear selection
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto pr-1">
        {loading ? (
          <div className="flex items-center justify-center py-24 text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading products…
          </div>
        ) : (
          <div className="grid grid-cols-3 xl:grid-cols-5 gap-2.5">
            {filtered.slice(0, 600).map((p) => {
              const on = picked.has(p.groupId);
              return (
                <div key={p.groupId}
                  className={`relative bg-white border-2 rounded-lg overflow-hidden transition ${
                    on ? 'border-indigo-600' : 'border-gray-200 hover:border-gray-300'}`}>
                  <button onClick={() => toggle(p.groupId)} className="block w-full text-left">
                    <div className="aspect-square bg-gray-50 flex items-center justify-center overflow-hidden">
                      {p.thumb ? <img src={p.thumb} alt="" loading="lazy" className="w-full h-full object-contain" />
                        : <ImageOff className="w-5 h-5 text-gray-300" />}
                    </div>
                    <div className="p-2">
                      <div className="text-[11px] font-medium text-gray-900 line-clamp-2 leading-snug min-h-[1.9rem]">
                        {p.series}
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs font-bold text-gray-900 flex items-baseline gap-1">
                          {fmtPrice(p.salePrice ?? p.price, p.currency)}
                          {p.salePrice != null && (
                            <span className="text-[10px] font-normal text-gray-400 line-through">
                              {fmtPrice(p.price, p.currency)}
                            </span>
                          )}
                        </span>
                        <span className="text-[10px] text-gray-400">{p.imageCount} img</span>
                      </div>
                    </div>
                  </button>
                  {on && (
                    <div className="absolute top-1.5 left-1.5 w-4 h-4 rounded-full bg-indigo-600 flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                  {p.salePrice != null && (
                    <span className="absolute top-1.5 right-1.5 px-1.5 py-0.5 text-[9px] font-bold bg-rose-600 text-white rounded">
                      %{discPct(p)}
                    </span>
                  )}
                </div>
              );
            })}
            {!filtered.length && (
              <div className="col-span-full py-20 text-center text-gray-400 text-sm">No product matches these filters.</div>
            )}
            {filtered.length > 600 && (
              <div className="col-span-full py-3 text-center text-xs text-gray-400">
                Showing the first 600 cards ({filtered.length} matches). “Select all shown” takes the whole
                match set.
              </div>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );

  // ============================================================ ADIM 2: ÇERÇEVE
  const stepTemplate = (
    <div>
      {/* 🔑 Çerçeve KAYNAKLARI en üstte, üç eşit kart (Efe, 14 Eyl: "design one
          with AI butonu daha görünür bir yerde olsun"). Eskiden sağ üstte
          küçücük üç düğmeydi ve kimse görmüyordu. */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <button
          onClick={() => setAiOpen((v) => !v)}
          className={`text-left rounded-xl border p-3.5 transition ${
            aiOpen ? 'border-violet-600 bg-violet-600 text-white shadow-md'
              : 'border-violet-300 bg-violet-50/60 hover:bg-violet-100/70'}`}>
          <div className={`flex items-center gap-2 font-semibold text-sm ${aiOpen ? 'text-white' : 'text-violet-900'}`}>
            <Sparkles className="w-4 h-4" /> Design one with AI
          </div>
          <div className={`text-[11px] mt-1 leading-snug ${aiOpen ? 'text-violet-100' : 'text-violet-700/80'}`}>
            Describe it in a sentence — three options come back in about 20 seconds.
          </div>
        </button>
        <button
          onClick={() => setUploadOpen(true)}
          disabled={frameUploading}
          className="text-left rounded-xl border border-indigo-300 bg-indigo-50/60 p-3.5 hover:bg-indigo-100/70 transition disabled:opacity-50">
          <div className="flex items-center gap-2 font-semibold text-sm text-indigo-900">
            {frameUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Upload your own frame
          </div>
          <div className="text-[11px] mt-1 leading-snug text-indigo-700/80">
            A PNG with a transparent window — square and 4:5 are taken separately.
          </div>
        </button>
        <button
          onClick={() => { setEditorFrame(null); setEditorOpen(true); }}
          className="text-left rounded-xl border border-emerald-300 bg-emerald-50/60 p-3.5 hover:bg-emerald-100/70 transition">
          <div className="flex items-center gap-2 font-semibold text-sm text-emerald-900">
            <PencilRuler className="w-4 h-4" /> Design it yourself
          </div>
          <div className="text-[11px] mt-1 leading-snug text-emerald-700/80">
            Lay out the product box, your logo and your own text on a canvas.
          </div>
        </button>
      </div>

      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="text-sm text-gray-600">
          One of the <span className="font-semibold text-gray-900">{picked.size}</span> products you picked
          was rendered in every template — click the one you like.
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          {sample && <span className="truncate max-w-[280px]">Sample: {sample.series}</span>}
          <button onClick={randomSample}
            className="flex items-center gap-1 px-2.5 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50">
            <Shuffle className="w-3.5 h-3.5" /> Another product
          </button>
        </div>
      </div>

      {/* Tarif → üç aday → biri seçilir. Seçilen aday yüklenmiş çerçeveyle aynı
          yola girer; buradan çıkan çerçeve de markaya kilitli. */}
      {aiOpen && (
        <div className="mb-3 border border-violet-200 bg-violet-50/60 rounded-lg p-3">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-violet-900 mb-2">
            <Sparkles className="w-4 h-4" /> Describe the frame you want
          </div>
          <textarea
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            rows={2}
            maxLength={600}
            placeholder="e.g. a warm back-to-school frame in mustard and deep navy, playful rounded shapes, confetti dots in the corners"
            className="w-full text-sm border border-violet-200 rounded-lg px-3 py-2 bg-white
                       focus:outline-none focus:ring-2 focus:ring-violet-300 resize-none" />
          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
            <label className="flex items-center gap-1.5 text-violet-900">
              <span>Shape</span>
              <select value={aiRatio} onChange={(e) => setAiRatio(e.target.value)}
                className="border border-violet-200 rounded px-1.5 py-1 bg-white">
                <option value="1:1">Square 1:1</option>
                <option value="4:5">Portrait 4:5</option>
                <option value="9:16">Story 9:16</option>
              </select>
            </label>
            <button
              onClick={generateFrames}
              disabled={aiBusy || aiPrompt.trim().length < 8}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-600 text-white
                         font-medium hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed">
              {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {aiBusy ? 'Drawing three options…' : 'Generate 3 options'}
            </button>
            <span className="text-violet-700/70">
              Takes about 20 seconds. Every option also comes in the other size
              (square ↔ 4:5), so you get both.
            </span>
          </div>

          {/* İsim/fiyat/rozet: çerçeve ölçülüp üstüne editörün tasarım belgesi
              kurulur — yani AI çerçevesi de sonradan düzenlenebilir. */}
          <div className="mt-2 rounded-lg bg-white/70 border border-violet-200 px-3 py-2">
            <label className="flex items-start gap-2 text-xs text-violet-900 cursor-pointer">
              <input type="checkbox" checked={aiComponents}
                onChange={(e) => setAiComponents(e.target.checked)}
                className="mt-0.5 rounded border-violet-300 text-violet-600 focus:ring-violet-500" />
              <span>
                <span className="font-semibold">Print the name and price on it too</span>
                <span className="block text-violet-700/80 leading-snug">
                  The name, the old price, the price and a discount badge are laid onto the frame
                  automatically, reading the right spot from the frame itself. Every product gets its own
                  values, and you can open the result in the canvas editor and move anything.
                </span>
              </span>
            </label>
            {aiComponents && (
              <input value={aiHeadline} onChange={(e) => setAiHeadline(e.target.value)}
                maxLength={60}
                placeholder="Optional headline for the top strip — e.g. SEASON SALE"
                className="mt-2 w-full text-xs border border-violet-200 rounded-lg px-2.5 py-1.5 bg-white
                           focus:outline-none focus:ring-2 focus:ring-violet-300" />
            )}
          </div>

          {aiBusy && (
            <div className="mt-3 grid grid-cols-3 gap-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="aspect-square rounded-lg bg-violet-100 animate-pulse" />
              ))}
            </div>
          )}

          {!aiBusy && aiDrafts && (
            <div className="mt-3 grid grid-cols-3 gap-3">
              {aiDrafts.map((d, i) => (
                <div key={d.draftId || i}
                  className="rounded-lg border border-violet-200 bg-white overflow-hidden flex flex-col">
                  {d.ok ? (
                    <>
                      {/* Şeffaf pencere görünsün diye dama zemin */}
                      <div className="relative bg-[length:16px_16px] bg-gray-100"
                        style={{ backgroundImage:
                          'linear-gradient(45deg,#e5e7eb 25%,transparent 25%,transparent 75%,#e5e7eb 75%),'
                          + 'linear-gradient(45deg,#e5e7eb 25%,transparent 25%,transparent 75%,#e5e7eb 75%)',
                        backgroundPosition: '0 0, 8px 8px' }}>
                        <img src={d.url} alt={d.style} className="w-full block" />
                        {/* Eş oran: aynı tasarım, o şekle YENİDEN KURGULANMIŞ hâli */}
                        {d.pair?.url && (
                          <img src={d.pair.url} alt=""
                            title={`Also drawn for ${d.pair.ratio}`}
                            className="absolute bottom-1.5 right-1.5 w-12 rounded border-2 border-white shadow-md bg-white" />
                        )}
                      </div>
                      <div className="px-2 pt-1.5 text-[10px] text-violet-700/80">
                        {d.pair?.ratio
                          ? <>Comes as a pair · {d.ratio || aiRatio} + {d.pair.ratio}</>
                          : <span className="text-amber-700">{d.ratio || aiRatio} only — the paired size could not be drawn</span>}
                      </div>
                      <button
                        onClick={() => adoptFrame(d)}
                        disabled={!!aiAdopting}
                        className="m-2 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg
                                   bg-violet-600 text-white text-xs font-medium hover:bg-violet-700
                                   disabled:opacity-40">
                        {aiAdopting === d.draftId
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <Check className="w-3.5 h-3.5" />}
                        Use this one
                      </button>
                    </>
                  ) : (
                    <div className="p-3 text-xs text-red-700">
                      <div className="flex items-center gap-1.5 font-semibold mb-1">
                        <AlertTriangle className="w-3.5 h-3.5" /> Option {i + 1} failed
                      </div>
                      <ul className="space-y-1 list-disc list-inside text-red-600">
                        {(d.errors || []).map((m, k) => <li key={k}>{m}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Standart dışı dosya: hangi kuralın düştüğü tek tek gösterilir, dosya kabul edilmez */}
      {frameReject && (
        <div className="mb-3 border border-red-200 bg-red-50 rounded-lg px-3 py-2.5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-1.5 text-sm font-semibold text-red-800">
                <AlertTriangle className="w-4 h-4" /> Frame not accepted
              </div>
              <ul className="mt-1.5 space-y-1 text-xs text-red-700 list-disc list-inside">
                {frameReject.errors.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
              <div className="mt-2 text-[11px] text-red-600/80">
                The full rule list is in the grey bar above. Nothing was uploaded — fix the file and try again.
              </div>
            </div>
            <button onClick={() => setFrameReject(null)} className="text-red-400 hover:text-red-700">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {galleryLoading && !gallery.length ? (
        <div className="flex items-center justify-center py-24 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Rendering templates…
        </div>
      ) : (
        <div className={`grid grid-cols-3 md:grid-cols-5 xl:grid-cols-6 gap-3 transition-opacity ${galleryLoading ? 'opacity-50' : ''}`}>
          {gallery.map((g) => {
            const t = templates.find((x) => x.id === g.templateId);
            const on = g.templateId === templateId;
            const short = sample && t && sample.imageCount < t.images;
            return (
              <div key={g.templateId}
                className={`group relative rounded-lg overflow-hidden border-2 transition cursor-pointer ${
                  on ? 'border-indigo-600 shadow-md' : 'border-gray-200 hover:border-gray-400'}`}
                onClick={() => setTemplateId(g.templateId)}>
                {/* rozet GÖRSELE bağlı, karta değil: özel çerçeveler artık kendi
                    en-boy oranında basılıyor, kart yüksekliği sabit değil */}
                <div className="relative">
                  <img src={g.url} alt={t?.name} className="w-full block" />
                  {t?.custom && (
                    <span className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 rounded bg-indigo-600/90 text-white text-[9px] font-semibold tracking-wide">
                      YOURS
                    </span>
                  )}
                </div>
                {on && (
                  <div className="absolute top-1.5 left-1.5 w-5 h-5 rounded-full bg-indigo-600 flex items-center justify-center shadow">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
                <div className="absolute top-1.5 right-1.5 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                  {t?.designed && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditorFrame({ id: g.templateId.replace(/^custom:/, ''), name: t.name });
                        setEditorOpen(true);
                      }}
                      title="Open this design in the editor"
                      className="p-1 rounded bg-white/90 border border-gray-200 hover:border-emerald-400">
                      <PencilRuler className="w-3.5 h-3.5 text-emerald-700" />
                    </button>
                  )}
                  {t?.custom && (
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteFrame(g.templateId); }}
                      title="Delete this frame"
                      className="p-1 rounded bg-white/90 border border-gray-200 hover:border-red-300">
                      <Trash2 className="w-3.5 h-3.5 text-red-600" />
                    </button>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); setZoom({ url: g.url, name: t?.name || g.templateId }); }}
                    title="Enlarge"
                    className="p-1 rounded bg-white/90 border border-gray-200">
                    <Maximize2 className="w-3.5 h-3.5 text-gray-700" />
                  </button>
                </div>
                <div className="px-2 py-1.5 bg-white" title={t?.desc}>
                  <div className="text-xs font-semibold text-gray-900 truncate">{t?.name || g.templateId}</div>
                  <div className="text-[10px] text-gray-400">
                    {t?.designed
                      ? 'you designed this · click the ruler to edit'
                      : t?.custom
                        ? 'uploaded frame · product only'
                        : <>{t?.images} image{t?.images > 1 ? 's' : ''}{short && <span className="text-amber-600"> · not enough</span>}</>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // =========================================================== ADIM 3: GÖRÜNÜM
  const customPicked = !!activeTemplate?.custom;
  const stepLook = (
    /* 🔑 Önizleme ORTADA ve BÜYÜK, ayarlar ALTINDA (Efe, 14 Eyl: "look kısmında
       preview ortada olsun büyük, diğer şeyler altında"). Eskiden sağda 320 px
       bir şeritti ve seçilen çerçeve gerçekte nasıl duruyor görünmüyordu. */
    <div className="max-w-[1060px] mx-auto pb-2">
      <div className="flex flex-col items-center">
        <div className="w-full max-w-[440px]">
          <div className="border border-gray-200 rounded-2xl overflow-hidden bg-gray-50 shadow-sm">
            {activePreview
              ? <img src={activePreview.url} alt=""
                  className={`w-full block transition-opacity ${galleryLoading ? 'opacity-40' : ''}`} />
              : <div className="aspect-[4/5] flex items-center justify-center text-gray-300">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>}
          </div>
          <div className="mt-2 flex items-center justify-center gap-3 text-xs text-gray-500">
            <span className="truncate">{activeTemplate?.name} · {sample?.series}</span>
            {galleryLoading && <Loader2 className="w-3 h-3 animate-spin shrink-0" />}
          </div>
          <button onClick={randomSample}
            className="mt-2 w-full flex items-center justify-center gap-1 px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50">
            <Shuffle className="w-3.5 h-3.5" /> Try another product
          </button>
        </div>
      </div>

      <div className="mt-6 space-y-4">
        {/* Yüklenen çerçeve pişmiş tasarımdır: renk/logo/kampanya hiçbir şeyi
            değiştirmez. Ayarları gizlemek yerine neden etkisiz olduğunu söylüyoruz
            — kullanıcı burada renk seçip sonuçta göremeyince araca güvenmiyor. */}
        {customPicked && (
          <div className="border border-indigo-200 bg-indigo-50 rounded-xl px-4 py-3 text-sm text-indigo-900">
            {activeTemplate.designed ? (
              <>
                <span className="font-semibold">{activeTemplate.name}</span> is a frame you laid out
                yourself. Colour, logo and campaign text below have
                <span className="font-semibold"> no effect</span> — everything, including which product
                fields get printed, is set inside the editor. Go back a step and click the ruler on the
                frame to change it.
              </>
            ) : (
              <>
                <span className="font-semibold">{activeTemplate.name}</span> is your own frame —
                it is printed exactly as uploaded. Colour, logo and campaign text below have
                <span className="font-semibold"> no effect</span>; only the product image is
                placed into the transparent window
                ({activeTemplate.fit === 'cover' ? 'filling the window, cropping the overflow'
                  : 'without cropping'}).
              </>
            )}
          </div>
        )}
        <div className="border border-gray-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <PaletteIcon className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-semibold text-gray-700">Colour</span>
            <span className="text-xs text-gray-400">a ready palette, or your own hex</span>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mb-3">
            {palette.map((p) => {
              const on = colors.join() === p.colors.join();
              return (
                <button key={p.id} title={p.name} onClick={() => setColors(p.colors)}
                  className={`w-8 h-8 rounded-full transition ${on ? 'ring-2 ring-offset-2 ring-indigo-600 scale-110' : 'ring-1 ring-gray-200'}`}
                  style={{ background: `linear-gradient(135deg, ${p.colors[0]}, ${p.colors[1]})` }} />
              );
            })}
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Custom:</span>
              {[0, 1].map((i) => (
                <div key={i} className="flex items-center gap-1">
                  <input type="color" value={HEX.test(colors[i]) ? colors[i] : '#000000'}
                    onChange={(e) => setColor(i, e.target.value)}
                    className="w-8 h-8 rounded cursor-pointer border border-gray-200 bg-white" />
                  <input value={colors[i]} onChange={(e) => setColor(i, e.target.value)}
                    className="w-24 px-2 py-1 text-xs font-mono border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Type className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-semibold text-gray-700">Price colour</span>
              <input type="color" value={HEX.test(priceColor) ? priceColor : '#ffffff'}
                onChange={(e) => setPriceColor(e.target.value)}
                className="w-8 h-8 rounded cursor-pointer border border-gray-200 bg-white" />
              <input value={priceColor} onChange={(e) => setPriceColor(e.target.value)}
                placeholder="auto"
                className="w-24 px-2 py-1 text-xs font-mono border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              {priceColor && (
                <button onClick={() => setPriceColor('')} className="text-xs text-gray-500 hover:text-gray-700 underline">
                  back to auto
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 🔑 Logo: onay kutusu değil AÇIK EYLEM (Efe, 14 Eyl: "logo bas diye bir
            şey var, tıklayınca hiçbir şey yapmıyor — logo ekle veya çıkart
            olmalı"). Logonun kendisi de gösteriliyor: basılıp basılmadığı
            önizlemeden önce burada belli olsun. */}
        <div className="border border-gray-200 rounded-xl p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden shrink-0">
                {effLogo
                  ? <img src={effLogo} alt="" className="max-w-full max-h-full object-contain" />
                  : <ImageOff className="w-4 h-4 text-gray-300" />}
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-700">Logo</div>
                <div className="text-[11px] text-gray-500">
                  {useLogo
                    ? (effLogo ? 'Printed on every frame' : 'Turned on, but no logo file yet')
                    : 'Not printed'}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input ref={logoFileRef} type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden"
                onChange={(e) => uploadBrandLogo(e.target.files?.[0])} />
              <button onClick={() => logoFileRef.current?.click()} disabled={logoBusy}
                title="Upload your logo once — it is kept for this brand"
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                {logoBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                Upload logo
              </button>
              {useLogo ? (
                <button onClick={() => setUseLogo(false)}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
                  Remove logo
                </button>
              ) : (
                <button onClick={() => setUseLogo(true)}
                  className="px-3 py-1.5 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                  Add logo
                </button>
              )}
            </div>
          </div>
          {useLogo && (
            <input value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)}
              placeholder="Logo URL (transparent PNG)"
              className="mt-3 w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          )}
          {useLogo && !effLogo && (
            <p className="text-[11px] text-amber-700 mt-2">
              No logo is stored for this brand — upload the file above, paste a URL, or leave it
              empty and the frames render without one.
            </p>
          )}
          {useLogo && !logoError && logoNote && (
            <p className="text-[11px] text-gray-500 mt-2">{logoNote}</p>
          )}
          {useLogo && logoError && (
            <p className="text-[11px] text-amber-700 mt-2 flex items-start gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{logoError}</span>
            </p>
          )}
          {useLogo && customPicked && (
            <p className="text-[11px] text-amber-700 mt-2">
              This frame is your own artwork, so the logo is not drawn on top of it — put the logo
              into the frame file itself, or use the canvas editor.
            </p>
          )}
        </div>

        {campFields.length > 0 && (
          <div className="border border-gray-200 rounded-xl p-4">
            <button onClick={() => setCampOpen((v) => !v)}
              className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <Megaphone className="w-4 h-4" /> Campaign text ({activeTemplate.name})
              <span className="font-normal text-xs text-gray-400">optional</span>
              {campOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {campOpen && (
              <>
                <div className="mt-3 flex flex-wrap gap-2">
                  {campFields.includes('badge') && ['badgeTop', 'badgeNum', 'badgeBottom'].map((k) => (
                    <input key={k} value={camp[k]} onChange={(e) => setCamp({ ...camp, [k]: e.target.value })}
                      placeholder={{ badgeTop: 'Badge top line', badgeNum: 'Badge number (9)', badgeBottom: 'Badge bottom line' }[k]}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg w-52 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                  ))}
                  {campFields.includes('promoLine') && (
                    <>
                      <input value={camp.line1} onChange={(e) => setCamp({ ...camp, line1: e.target.value })}
                        placeholder="Campaign line" className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg w-64 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                      <input value={camp.pct} onChange={(e) => setCamp({ ...camp, pct: e.target.value })}
                        placeholder="% (50)" className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg w-24 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                      <input value={camp.suffix} onChange={(e) => setCamp({ ...camp, suffix: e.target.value })}
                        placeholder="Suffix word" className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg w-36 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                    </>
                  )}
                  {campFields.includes('promoText') && (
                    <>
                      <input value={camp.promoPre} onChange={(e) => setCamp({ ...camp, promoPre: e.target.value })}
                        placeholder="White part (UP TO 50%)" className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg w-56 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                      <input value={camp.promoHi} onChange={(e) => setCamp({ ...camp, promoHi: e.target.value })}
                        placeholder="Red part (OFF)" className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg w-48 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                    </>
                  )}
                </div>
                <p className="text-[11px] text-amber-700 mt-2">
                  These lines are printed onto the image exactly as typed — make sure the campaign is
                  actually running. Leave them empty and the campaign area is never drawn.
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // ============================================================== ADIM 4: ÜRET
  const stepBuild = (
    <div className="grid grid-cols-[1fr_320px] gap-6 items-start max-w-4xl mx-auto">
      <div className="space-y-4">
        <div className="border border-gray-200 rounded-xl divide-y divide-gray-100">
          {[
            ['Products', `${picked.size} product groups`, () => setStep(1)],
            ['Frame', activeTemplate?.name || '—', () => setStep(2)],
            ['Look', `${paletteName}${priceColor ? ` · price ${priceColor}` : ''}${effLogo ? ' · with logo' : ''}`, () => setStep(3)],
            ['Variants', allVariants ? 'All variants' : 'One per colour', () => setStep(1)],
          ].map(([k, v, go]) => (
            <div key={k} className="flex items-center justify-between px-4 py-2.5">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{k}</span>
              <span className="text-sm text-gray-900 flex items-center gap-2">
                {v}
                <button onClick={go} className="text-[11px] text-indigo-600 hover:underline">change</button>
              </span>
            </div>
          ))}
        </div>

        {thinPicks > 0 && (
          <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5 leading-snug">
            {thinPicks} of your picks do not have {needImgs} images — the missing slots will repeat an image.
          </div>
        )}

        <div>
          <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Feed name
          </label>
          <input value={autoSlug}
            onChange={(e) => { setSlugTouched(true); setSlug(e.target.value); }}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          <div className="flex items-center justify-between mt-1">
            <span className="text-[10px] text-gray-400">
              {slugTouched ? 'edited by hand' : 'derived from template + colour + filters'}
            </span>
            {slugTouched && (
              <button onClick={() => { setSlugTouched(false); setSlug(''); }}
                className="text-[10px] text-indigo-600 hover:underline">back to auto</button>
            )}
          </div>
        </div>

        <button onClick={generate} disabled={!picked.size || !templateId || job?.status === 'running'}
          className="w-full py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
          {job?.status === 'running'
            ? <><Loader2 className="w-4 h-4 animate-spin" /> Building…</>
            : <><Rocket className="w-4 h-4" /> Build the frame</>}
        </button>

        {job?.status === 'running' && (
          <p className="text-[11px] text-gray-500 leading-snug">
            Processing {job.total} products. You can close this window — the build carries on server side
            and the finished feed shows up in the list below.
          </p>
        )}
        {job?.status === 'error' && <p className="text-[11px] text-red-600 break-words">{job.error}</p>}
        {job?.status === 'done' && (
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <div className="text-xs text-emerald-800 font-semibold mb-1.5">
              {job.count} products ready{job.failed ? ` · ${job.failed} failed` : ''}
            </div>
            <div className="flex items-center gap-1">
              <input readOnly value={job.feedUrl}
                className="flex-1 min-w-0 text-[11px] px-2 py-1 bg-white border border-emerald-200 rounded" />
              <button onClick={() => copy(job.feedUrl)} className="p-1.5 hover:bg-emerald-100 rounded" title="Copy">
                <Copy className="w-3.5 h-3.5 text-emerald-700" />
              </button>
              <a href={job.feedUrl} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-emerald-100 rounded" title="Open">
                <ExternalLink className="w-3.5 h-3.5 text-emerald-700" />
              </a>
            </div>
            <button onClick={() => setWizard(false)}
              className="mt-2 w-full py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700">
              Done — back to the list
            </button>
          </div>
        )}
        <p className="text-[11px] text-gray-400 leading-snug">
          Once built, the feed refreshes itself every 2 hours: if a price or discount changes the image is
          re-rendered, and products that go out of stock drop out of the feed.
        </p>
      </div>

      <div className="sticky top-0">
        <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Sample frame</div>
        <div className="border border-gray-200 rounded-xl overflow-hidden bg-gray-50">
          {activePreview
            ? <img src={activePreview.url} alt="" className="w-full block" />
            : <div className="aspect-square flex items-center justify-center text-gray-300"><ImageOff className="w-6 h-6" /></div>}
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      {/* ---------------- başlık ---------------- */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <img
              src={HERO_ICON}
              alt=""
              className="h-9 w-9 shrink-0 select-none"
              style={{ filter: 'drop-shadow(0 3px 6px rgba(79,70,229,0.28))' }}
            />
            Glowy Studio
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Drops your Merchant Center products into frames, scenes and photo backdrops, then builds a
            product catalogue you can run on Meta and Display.
          </p>
          {status && (
            <p className="text-xs text-gray-400 mt-1">
              {status.brand.name} · MC {status.merchantId} · {status.rows} rows
              {status.lastFetch && <> · son senkron {String(status.lastFetch).slice(0, 10)}</>}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={load} disabled={loading}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button onClick={openWizard} disabled={loading || !products.length}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed">
            <Plus className="w-4 h-4" /> New frame
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /><span className="break-words">{error}</span>
        </div>
      )}

      {/* ---------------- kapak: ürün → çerçeve → bilgi ---------------- */}
      <section
        className="relative overflow-hidden rounded-3xl border border-gray-200 bg-[#FAFAFB] mb-6
                   px-5 py-8 sm:px-8 sm:py-10"
        style={{
          backgroundImage:
            'linear-gradient(rgba(15,23,42,0.045) 1px, transparent 1px),' +
            'linear-gradient(90deg, rgba(15,23,42,0.045) 1px, transparent 1px)',
          backgroundSize: '34px 34px',
        }}
      >
        {/* zemini kenarlara doğru söndür — ızgara kutunun içinde kaybolsun */}
        <div className="pointer-events-none absolute inset-0"
             style={{ background: 'radial-gradient(120% 90% at 50% 30%, transparent 35%, #FAFAFB 100%)' }} />

        <div className="relative">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <h2 className="text-2xl sm:text-[32px] font-bold tracking-tight text-gray-900 leading-[1.25]">
              A plain catalogue photo <HeroIcon k="foto" />
              <br className="hidden sm:block" /> turned into an ad you can run <HeroIcon k="etiket" />
            </h2>
          </div>

          {HERO_ROWS.map((row, ri) => (
            <div key={row.key} className={ri ? 'mt-8 pt-8 border-t border-gray-200/70' : ''}>
              {/* masaüstü: oklarla yatay ilerleyiş */}
              <div className="hidden md:flex items-start justify-center gap-1 lg:gap-2">
                <HeroShot shot={row.shots[0]} />
                <HeroArrow label={row.arrows[0]} />
                <HeroShot shot={row.shots[1]} />
                <HeroArrow label={row.arrows[1]} />
                <HeroShot shot={row.shots[2]} />
              </div>

              {/* mobil: dikey ilerleyiş, ok aşağı döner */}
              <div className="md:hidden flex flex-col items-center gap-3">
                {row.shots.map((shot, i) => (
                  <React.Fragment key={shot.n}>
                    <div className="w-full max-w-[220px]"><HeroShot shot={shot} /></div>
                    {i < row.shots.length - 1 && (
                      <div className="flex items-center gap-2 text-gray-400">
                        <svg viewBox="0 0 24 34" className="w-5 h-7" fill="none" aria-hidden="true">
                          <path d="M12 3 C 5 12, 19 20, 12 29" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
                          <path d="M6 23 L12 30 L18 23" stroke="currentColor" strokeWidth="2.4"
                                strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span className="text-[12px] font-medium text-gray-500">{row.arrows[i]}</span>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          ))}

          <div className="flex justify-center mt-8">
            <img src="/Calisma-Yuzeyi-20@5x-4.png" alt="Glowytics" className="h-7 w-auto" />
          </div>
        </div>
      </section>

      {/* ---------------- nasıl çalışır ---------------- */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        {STEPS.map((s, i) => (
          <div key={s.n} className="flex items-start gap-2.5 bg-white border border-gray-200 rounded-xl p-3">
            <div className="w-6 h-6 rounded-full bg-indigo-50 text-indigo-700 text-xs font-bold flex items-center justify-center shrink-0">
              {s.n}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-900 flex items-center gap-1">
                {s.label}
                {i === 0 && <MousePointerClick className="w-3.5 h-3.5 text-gray-300" />}
              </div>
              <div className="text-[11px] text-gray-500 leading-snug">{s.hint}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ---------------- üretilmiş feed'ler ---------------- */}
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <FileCode2 className="w-4 h-4" /> Feed’lerin
          <span className="font-normal text-gray-400">({feeds.length})</span>
        </div>
      </div>

      {loading && !feeds.length ? (
        <div className="flex items-center justify-center py-20 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
        </div>
      ) : feeds.length === 0 ? (
        <div className="border-2 border-dashed border-gray-200 rounded-2xl py-16 px-6 text-center">
          <Sparkles className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-gray-900 mb-1">No frames yet</h3>
          <p className="text-sm text-gray-500 max-w-md mx-auto mb-5">
            Pick your products, choose a frame template, set its colours — in minutes you have a framed
            product feed ready to run as advertising.
          </p>
          <button onClick={openWizard} disabled={loading || !products.length}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-40">
            <Plus className="w-4 h-4" /> New frame
          </button>
          {!!products.length && (
            <p className="text-xs text-gray-400 mt-3">{facets.total} product groups ready and waiting.</p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {feeds.map((f) => {
            const tpl = templates.find((t) => t.id === f.templateId);
            return (
              <div key={f.slug} className="bg-white border border-gray-200 rounded-xl p-3.5 hover:border-gray-300 transition">
                <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-lg shrink-0"
                  style={{ background: `linear-gradient(135deg, ${f.colors?.[0] || '#999'}, ${f.colors?.[1] || '#ccc'})` }} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-gray-900 truncate" title={f.name || f.slug}>
                    {f.name || f.slug}
                  </div>
                  <div className="text-[11px] text-gray-500 mt-0.5">
                    {tpl?.name || f.templateId} · {f.productCount} products
                    {f.failedCount ? <span className="text-amber-600"> · {f.failedCount} hata</span> : null}
                    {f.allVariants ? ' · all variants' : ''}
                  </div>
                  <div className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-1 flex-wrap">
                    {f.autoRefresh
                      ? <span className="inline-flex items-center gap-0.5 text-emerald-600"><Clock className="w-3 h-3" /> otomatik tazeleniyor</span>
                      : <span className="text-gray-400">auto-refresh off</span>}
                    {(f.lastBuiltAt || f.createdAt) && <>
                      · {new Date(f.lastBuiltAt || f.createdAt).toLocaleString('tr-TR',
                        { timeZone: 'Europe/Istanbul', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </>}
                    {f.sizeBytes ? ` · ${Math.round(f.sizeBytes / 1024)} KB` : ''}
                    {f.exists === false && <span className="text-red-500"> · dosya yok</span>}
                  </div>
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  <button onClick={() => copy(f.feedUrl)} className="p-1.5 hover:bg-gray-100 rounded" title="Copy link">
                    <Copy className="w-3.5 h-3.5 text-gray-600" />
                  </button>
                  <a href={f.feedUrl} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-gray-100 rounded" title="Open">
                    <ExternalLink className="w-3.5 h-3.5 text-gray-600" />
                  </a>
                  <button onClick={() => deleteFeed(f.slug)} className="p-1.5 hover:bg-red-50 rounded" title="Delete">
                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                  </button>
                </div>
                </div>

                {/* feed'in ilk 5 karesi — tek sıra, taşma yok */}
                {f.samples?.length > 0 && (
                  <a href={f.feedUrl} target="_blank" rel="noreferrer"
                    className="mt-3 grid grid-cols-5 gap-2" title="Open feed">
                    {f.samples.slice(0, 5).map((src, i) => (
                      <div key={i} className="aspect-square rounded-lg overflow-hidden bg-gray-50 border border-gray-100">
                        <img src={src} alt="" loading="lazy" className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ================================================= SİHİRBAZ ================================================= */}
      {wizard && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setWizard(false); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[1400px] h-[92vh] flex flex-col overflow-hidden">
            {/* başlık + adımlar */}
            <div className="px-5 py-3.5 border-b border-gray-200 flex items-center gap-6">
              <div className="shrink-0">
                <div className="text-sm font-bold text-gray-900">New frame</div>
                <div className="text-[11px] text-gray-400">{STEPS[step - 1].hint}</div>
              </div>
              <div className="flex items-center gap-1 flex-1 min-w-0 overflow-x-auto">
                {STEPS.map((s, i) => {
                  const done = s.n < step;
                  const active = s.n === step;
                  const reachable = s.n < step || (s.n === step);
                  return (
                    <React.Fragment key={s.n}>
                      {i > 0 && <div className={`h-px flex-1 min-w-[16px] ${done || active ? 'bg-indigo-300' : 'bg-gray-200'}`} />}
                      <button onClick={() => reachable && setStep(s.n)} disabled={!reachable}
                        className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-sm whitespace-nowrap transition ${
                          active ? 'bg-indigo-50 text-indigo-700 font-semibold'
                            : done ? 'text-gray-600 hover:bg-gray-50' : 'text-gray-300 cursor-default'}`}>
                        <span className={`w-5 h-5 rounded-full text-[11px] font-bold flex items-center justify-center ${
                          active ? 'bg-indigo-600 text-white'
                            : done ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-400'}`}>
                          {done ? <Check className="w-3 h-3" /> : s.n}
                        </span>
                        {s.label}
                      </button>
                    </React.Fragment>
                  );
                })}
              </div>
              <button onClick={() => setWizard(false)} className="p-1.5 rounded-lg hover:bg-gray-100 shrink-0" title="Close">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* gövde
                🔑🔑 Gövdenin KENDİSİ kaydırmaz (Efe, 14 Eyl: "scroll ettikçe
                sadece itemlar aşağı inecek, soldaki seçenekler freeze olsun").
                Her adım kendi kaydırma alanını kurar; 1. adımda filtre paneli
                ile ürün ızgarası AYRI kaydırılır. */}
            <div className="flex-1 min-h-0 p-5 overflow-hidden">
              {step === 1 && stepProducts}
              {step !== 1 && (
                <div className="h-full overflow-y-auto pr-1">
                  {step === 2 && stepTemplate}
                  {step === 3 && stepLook}
                  {step === 4 && stepBuild}
                </div>
              )}
            </div>

            {/* Kendi çerçeveni yükle — KARE ve 4:5 AYRI (Efe, 14 Eyl:
                "upload kısmında kareyi ayrı 4:5'i ayrı alalım, onu da popup'ta
                iste"). İki dosya da zorunlu değil; hangisi verildiyse o yüklenir. */}
            {uploadOpen && (
              <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
                onMouseDown={(e) => { if (e.target === e.currentTarget) setUploadOpen(false); }}>
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[720px] max-h-[90vh] flex flex-col overflow-hidden">
                  <div className="px-5 py-3.5 border-b border-gray-200 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-bold text-gray-900">Upload your own frame</div>
                      <div className="text-[11px] text-gray-400">
                        A PNG with a transparent window. Only the product image goes inside — no name or price.
                      </div>
                    </div>
                    <button onClick={() => setUploadOpen(false)} className="p-1.5 rounded-lg hover:bg-gray-100">
                      <X className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>

                  <div className="p-5 overflow-y-auto space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      {UPLOAD_SLOTS.map((slot) => {
                        const f = upFiles[slot.key];
                        const off = f?.aspect ? Math.abs(f.aspect - slot.want) / slot.want > 0.03 : false;
                        const ref = slot.key === '1:1' ? frameFileRef : frameFileRef45;
                        return (
                          <div key={slot.key} className="border border-gray-200 rounded-xl p-3">
                            <div className="text-sm font-semibold text-gray-800">{slot.label}</div>
                            <div className="text-[11px] text-gray-400 mb-2">{slot.hint}</div>
                            <input
                              ref={ref} type="file" accept="image/png" className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                const aspect = await readAspect(file);
                                setUpFiles((c) => ({ ...c, [slot.key]: { file, aspect } }));
                              }} />
                            <button
                              onClick={() => ref.current?.click()}
                              className={`w-full rounded-lg border-2 border-dashed px-3 py-6 text-xs transition ${
                                f ? 'border-indigo-300 bg-indigo-50/50 text-indigo-800'
                                  : 'border-gray-300 text-gray-500 hover:border-gray-400 hover:bg-gray-50'}`}>
                              {f
                                ? <span className="font-medium break-all">{f.file.name}</span>
                                : <span className="flex flex-col items-center gap-1">
                                    <Upload className="w-4 h-4" /> Choose a PNG
                                  </span>}
                            </button>
                            {f && (
                              <div className="mt-1.5 flex items-center justify-between gap-2">
                                <span className="text-[11px] text-gray-500">
                                  {f.aspect ? `ratio ${f.aspect.toFixed(2)}` : 'ratio unknown'}
                                </span>
                                <button onClick={() => setUpFiles((c) => ({ ...c, [slot.key]: null }))}
                                  className="text-[11px] text-gray-500 hover:text-gray-800 underline">
                                  remove
                                </button>
                              </div>
                            )}
                            {off && (
                              <p className="mt-1.5 text-[11px] text-amber-700 leading-snug">
                                This file is not {slot.label.split('·')[1].trim()} — it will still be uploaded
                                at its own size, the canvas follows the frame.
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                      <label className="flex items-center gap-1.5">
                        <span className="text-gray-600 font-medium">Product fit</span>
                        <select value={frameFit} onChange={(e) => setFrameFit(e.target.value)}
                          className="border border-gray-300 rounded px-1.5 py-0.5 bg-white">
                          <option value="contain">Never crop (pad)</option>
                          <option value="cover">Fill the window (crop)</option>
                        </select>
                      </label>
                      {frameSpec.length > 0 && <span className="text-gray-400">Rules: {frameSpec.join(' · ')}</span>}
                    </div>
                  </div>

                  <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      You can upload just one of them — the other can follow later.
                    </span>
                    <button
                      disabled={frameUploading || (!upFiles['1:1'] && !upFiles['4:5'])}
                      onClick={async () => {
                        let ok = 0;
                        for (const slot of UPLOAD_SLOTS) {
                          const f = upFiles[slot.key];
                          if (!f) continue;
                          // sırayla: ikisi de aynı anda gidince galeri iki kez basılıyor
                          // eslint-disable-next-line no-await-in-loop
                          if (await uploadFrame(f.file, slot.key)) ok += 1;
                        }
                        if (ok) { setUpFiles({ '1:1': null, '4:5': null }); setUploadOpen(false); }
                      }}
                      className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed">
                      {frameUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      Upload
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* büyütülmüş önizleme */}
            {zoom && (
              <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-6"
                onClick={() => setZoom(null)}>
                <div className="max-h-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
                  <img src={zoom.url} alt={zoom.name} className="max-h-[80vh] w-auto rounded-lg shadow-2xl" />
                  <div className="mt-3 flex items-center gap-3">
                    <span className="text-sm text-white font-semibold">{zoom.name}</span>
                    <button onClick={() => setZoom(null)}
                      className="px-3 py-1.5 text-xs bg-white/90 rounded-lg hover:bg-white">Close</button>
                  </div>
                </div>
              </div>
            )}

            {/* alt bar */}
            <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between gap-4 bg-gray-50">
              <button onClick={goBack} disabled={step === 1}
                className="flex items-center gap-1.5 px-3.5 py-2 text-sm border border-gray-300 bg-white rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>

              <div className="text-xs text-gray-500 truncate">
                {step === 1 && (picked.size
                  ? <><span className="font-semibold text-gray-900">{picked.size}</span> products selected</>
                  : 'Select at least one product to continue')}
                {step === 2 && (activeTemplate ? <>Template: <span className="font-semibold text-gray-900">{activeTemplate.name}</span></> : 'Choose a template')}
                {step === 3 && 'All optional — you can walk straight past this step'}
                {step === 4 && `${picked.size} products · ${activeTemplate?.name || ''}`}
              </div>

              {step < 4 ? (
                <button onClick={goNext} disabled={!canNext}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed">
                  Next <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button onClick={() => setWizard(false)}
                  className="px-4 py-2 text-sm border border-gray-300 bg-white rounded-lg hover:bg-gray-50">
                  Kapat
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tuval editörü. Sihirbazın ÜSTÜNDE açılır; kapanınca kullanıcı bıraktığı
          adıma geri döner. Canlı örnek ürün SEÇİLENLERDEN gelir — kullanıcı
          tasarımı kendi kataloğu üstünde görsün. */}
      <FrameEditor
        open={editorOpen}
        onClose={() => { setEditorOpen(false); setEditorFrame(null); }}
        brand={status?.brand || null}
        brandHeader={brandHeader}
        products={editorProducts}
        editFrame={editorFrame}
        onSaved={onDesignSaved}
      />
    </div>
  );
}
